import { useEffect, useRef } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { BASE_URL, tokenStore } from '../services/api-client';
import type { AppNotification } from '../services/notifications.service';

/**
 * Subscribes to the backend's Server-Sent Events notification stream for
 * the lifetime of the calling component.
 *
 * <p>Uses {@code @microsoft/fetch-event-source} (not the native
 * {@code EventSource}) so the JWT can travel in the {@code Authorization}
 * header — the same auth path as every other API call, no token in the
 * URL. The library reconnects automatically with backoff if the stream
 * drops; the polling fallback on the unread-count query covers any gap.</p>
 *
 * @param onNotification invoked with each freshly pushed notification.
 */
export function useNotificationStream(onNotification: (n: AppNotification) => void) {
  // Keep the latest callback in a ref so the effect opens the stream once,
  // not on every render.
  const callbackRef = useRef(onNotification);
  callbackRef.current = onNotification;

  useEffect(() => {
    const token = tokenStore.getAccess();
    if (!token) return undefined;

    const controller = new AbortController();

    fetchEventSource(`${BASE_URL}/notifications/stream`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
      // Keep receiving while the tab is backgrounded — the badge stays current.
      openWhenHidden: true,
      onmessage(ev) {
        if (ev.event !== 'notification' || !ev.data) return;
        try {
          callbackRef.current(JSON.parse(ev.data) as AppNotification);
        } catch {
          // Ignore malformed frames — the count query will still reconcile.
        }
      },
      onerror() {
        // Returning lets the library retry with backoff; throwing would
        // stop the stream for good.
      },
    });

    return () => controller.abort();
  }, []);
}
